import { Sparkles, Zap, Crown, Users } from 'lucide-react-native'
import type { Translations } from '../../i18n/translations'
import { CONSUMER_PRO_BILLING_INTERVAL } from '../constants/consumerProBilling.constants'
import { PRICING_PLAN_TIERS } from '../constants/pricingPlanTiers.constants'
import { CONSUMER_PRO_DISPLAY_PRICES } from '../constants/pricingPlanTiers.constants'
import type {
  BusinessSeatBucket,
  PricingPlanCard,
  PricingPlanFeature,
} from '../constants/pricingPlans.types'

export type PricingCopyLabels = Translations['pricing']['copy']

function planFeatures(labels: readonly string[]): PricingPlanFeature[] {
  return labels.map((label) => ({ label, included: true }))
}

export function getPricingCopy(t: Translations): PricingCopyLabels {
  return t.pricing.copy
}

export function buildBusinessSeatBuckets(t: Translations): readonly BusinessSeatBucket[] {
  const b = t.pricing.seatBuckets
  return [
    { key: 'BUSINESS_100', maxSeats: 100, priceLabel: '$450', tagline: b.upTo100 },
    { key: 'BUSINESS_250', maxSeats: 250, priceLabel: '$550', tagline: b.upTo250 },
    { key: 'BUSINESS_500', maxSeats: 500, priceLabel: '$800', tagline: b.upTo500 },
    { key: 'BUSINESS_1000', maxSeats: 1000, priceLabel: '$1,200', tagline: b.upTo1000 },
    { key: 'BUSINESS_2000', maxSeats: 2000, priceLabel: '$1,700', tagline: b.upTo2000 },
  ] as const
}

export function buildPricingPlanCards(t: Translations): PricingPlanCard[] {
  const p = t.pricing.plans
  const prices = CONSUMER_PRO_DISPLAY_PRICES

  return [
    {
      tier: PRICING_PLAN_TIERS.HOBBY,
      name: p.hobby.name,
      priceLabel: p.hobby.priceLabel,
      tagline: p.hobby.tagline,
      icon: Sparkles,
      features: planFeatures(p.hobby.features),
      ctaLabel: p.hobby.ctaLabel,
    },
    {
      tier: PRICING_PLAN_TIERS.PRO_MONTHLY,
      name: p.proMonthly.name,
      priceLabel: prices.MONTHLY_LABEL,
      priceSubLabel: t.pricing.perMonth,
      tagline: p.proMonthly.tagline,
      icon: Zap,
      highlighted: true,
      mostPopular: true,
      consumerProCheckout: true,
      consumerProBillingInterval: CONSUMER_PRO_BILLING_INTERVAL.MONTH,
      features: planFeatures(p.proMonthly.features),
      ctaLabel: p.proMonthly.ctaLabel,
    },
    {
      tier: PRICING_PLAN_TIERS.PRO_YEARLY,
      name: p.proYearly.name,
      priceLabel: prices.YEARLY_LABEL,
      priceSubLabel: t.pricing.perYear,
      tagline: p.proYearly.tagline,
      icon: Zap,
      consumerProCheckout: true,
      consumerProBillingInterval: CONSUMER_PRO_BILLING_INTERVAL.YEAR,
      features: planFeatures(p.proYearly.features),
      ctaLabel: p.proYearly.ctaLabel,
    },
    {
      tier: PRICING_PLAN_TIERS.BUSINESS,
      name: p.business.name,
      priceLabel: p.business.priceLabel,
      tagline: p.business.tagline,
      icon: Users,
      requiresOrg: true,
      ctaLabel: p.business.ctaLabel,
      features: planFeatures(p.business.features),
    },
    {
      tier: PRICING_PLAN_TIERS.ENTERPRISE,
      name: p.enterprise.name,
      priceLabel: p.enterprise.priceLabel,
      tagline: p.enterprise.tagline,
      icon: Crown,
      contactSales: true,
      requiresOrg: true,
      features: planFeatures(p.enterprise.features),
      ctaLabel: p.enterprise.ctaLabel,
    },
  ]
}

export function buildUpgradeMonthlyToYearlyProDialogMessage(t: Translations): string {
  const { MONTHLY_LABEL, YEARLY_LABEL } = CONSUMER_PRO_DISPLAY_PRICES
  const d = t.pricing.copy.upgradeMonthlyToYearlyDialog
  return [
    d.line1.replace('{monthly}', MONTHLY_LABEL).replace('{yearly}', YEARLY_LABEL),
    '',
    d.line2,
    '',
    d.line3,
    '',
    d.line4,
  ].join('\n')
}

/** Maps i18n pricing copy to legacy UPPER_SNAKE keys used in older call sites. */
export function mapPricingCopyToLegacy(copy: PricingCopyLabels) {
  return {
    PAGE_TITLE: copy.pageTitle,
    PAGE_SUBTITLE: copy.pageSubtitle,
    COMING_SOON_BADGE: copy.comingSoonBadge,
    COMING_SOON_IN_PRODUCTION: copy.comingSoonInProduction,
    MOST_POPULAR_BADGE: copy.mostPopularBadge,
    CURRENT_PLAN_BADGE: copy.currentPlanBadge,
    CREATE_ORG_CTA: copy.createOrgCta,
    FOOTNOTE: copy.footnote,
    FOOTNOTE_CANCEL_POLICY: copy.footnoteCancelPolicy,
    SIGN_IN_TO_SUBSCRIBE: copy.signInToSubscribe,
    LOG_IN: copy.logIn,
    GO_BACK: copy.goBack,
    MANAGE_SUBSCRIPTION: copy.manageSubscription,
    PRO_ACCESS_ENDS_ON: copy.proAccessEndsOn,
    BILLING_INTERVAL_MONTH: copy.billingIntervalMonth,
    BILLING_INTERVAL_YEAR: copy.billingIntervalYear,
    SWITCH_TO_MONTHLY: copy.switchToMonthly,
    UPGRADE_TO_YEARLY_DIALOG_MSG: copy.upgradeToYearlyDialogMsg,
    UPGRADE_TO_YEARLY_DIALOG_CONFIRM: copy.upgradeToYearlyDialogConfirm,
    UPGRADE_TO_YEARLY_DIALOG_CANCEL: copy.upgradeToYearlyDialogCancel,
    SWITCH_TO_MONTHLY_DIALOG_TITLE: copy.switchToMonthlyDialogTitle,
    SWITCH_TO_MONTHLY_DIALOG_MSG: copy.switchToMonthlyDialogMsg,
    SWITCH_TO_MONTHLY_CONFIRM: copy.switchToMonthlyConfirm,
    SWITCH_TO_MONTHLY_CANCEL: copy.switchToMonthlyCancel,
    UPGRADE_TO_YEARLY_PRO_TITLE: copy.upgradeToYearlyProTitle,
    UPGRADE_TO_YEARLY_PRO_CONFIRM: copy.upgradeToYearlyProConfirm,
    ENTERPRISE_SUBSCRIBE: copy.enterpriseSubscribe,
    ENTERPRISE_CHECKOUT_ADMIN_ONLY: copy.enterpriseCheckoutAdminOnly,
    UPGRADE_SEAT_BUCKET: copy.upgradeSeatBucket,
    MANAGE_PLAN: copy.managePlan,
    BUSINESS_SUBSCRIBE: copy.businessSubscribe,
  } as const
}

export type LegacyPricingCopy = ReturnType<typeof mapPricingCopyToLegacy>
